import { SITE_URL, SITE_NAME } from "@/lib/site";
import { CONTACT, LEGAL } from "@/lib/policyMeta";
import {
  BRAND_ALTERNATE_NAMES,
  BRAND_KEYWORDS,
  DEFAULT_OG_IMAGE,
  OFFICE_GEO,
  SERVICE_AREA_CITIES,
  absoluteUrl,
} from "@/lib/seoConfig";

function areaServedPlaces() {
  return SERVICE_AREA_CITIES.map((city) => ({
    "@type": "City",
    name: city,
    containedInPlace: {
      "@type": "State",
      name: "Maharashtra",
      containedInPlace: { "@type": "Country", name: "India" },
    },
  }));
}

export function brandSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Brand",
    "@id": `${SITE_URL}/#brand`,
    name: "OORI Label",
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    slogan: "OORI means home",
    description:
      "OORI Label is a Mumbai-based contemporary Indian women's fashion brand. OORI means home in Hindi — kurtis, co-ords, dresses and tops made to be lived in.",
  };
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "OORI Label",
    legalName: LEGAL.entity,
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    description:
      "OORI Label (OORI) — official store. OORI means home. Contemporary Indian fashion for women — kurtis, co-ords, dresses and tops made to be lived in.",
    email: CONTACT.email,
    telephone: CONTACT.whatsapp.replace(/\s/g, ""),
    sameAs: [CONTACT.whatsappHref, SITE_URL],
    brand: { "@id": `${SITE_URL}/#brand` },
    keywords: BRAND_KEYWORDS.join(", "),
    address: {
      "@type": "PostalAddress",
      addressLocality: "Mumbai",
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ClothingStore",
    "@id": `${SITE_URL}/#store`,
    name: "OORI Label",
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    image: DEFAULT_OG_IMAGE,
    logo: DEFAULT_OG_IMAGE,
    description:
      "Official OORI Label online boutique — contemporary Indian women's fashion: kurtis, co-ords, tops & dresses. Based in Mumbai, Maharashtra.",
    email: CONTACT.email,
    telephone: CONTACT.whatsapp.replace(/\s/g, ""),
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "Credit Card, Debit Card, UPI, Net Banking, Cash on Delivery",
    brand: { "@id": `${SITE_URL}/#brand` },
    address: {
      "@type": "PostalAddress",
      addressLocality: LEGAL.jurisdictionCity,
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: OFFICE_GEO.latitude,
      longitude: OFFICE_GEO.longitude,
    },
    areaServed: areaServedPlaces(),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "OORI Label Women's Fashion",
      itemListElement: [
        { "@type": "OfferCatalog", name: "Kurtis & Ethnic" },
        { "@type": "OfferCatalog", name: "Co-ords" },
        { "@type": "OfferCatalog", name: "Tops" },
        { "@type": "OfferCatalog", name: "Dresses" },
      ],
    },
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "OORI Label",
    alternateName: BRAND_ALTERNATE_NAMES,
    url: SITE_URL,
    description:
      "Official website of OORI Label (OORI) — shop contemporary Indian women's fashion online.",
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-IN",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function breadcrumbSchema(items) {
  if (!items?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function productSchema(product, imageUrl) {
  if (!product?.id) return null;
  const images = [];
  if (imageUrl) images.push(imageUrl);
  const inStock = (product.stock_count ?? 0) > 0;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": absoluteUrl(`/product/${product.id}#product`),
    name: product.name,
    description: product.description || `${product.name} — ${product.category_name || "Fashion"} at ${SITE_NAME}`,
    image: images.length ? images : [DEFAULT_OG_IMAGE],
    sku: product.id,
    brand: {
      "@type": "Brand",
      "@id": `${SITE_URL}/#brand`,
      name: "OORI Label",
      alternateName: BRAND_ALTERNATE_NAMES,
    },
    category: product.category_name || product.category,
    offers: {
      "@type": "Offer",
      url: absoluteUrl(`/product/${product.id}`),
      priceCurrency: "INR",
      price: product.price,
      availability: inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      seller: { "@id": `${SITE_URL}/#store` },
      areaServed: {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: OFFICE_GEO.latitude,
          longitude: OFFICE_GEO.longitude,
        },
        geoRadius: `${OFFICE_GEO.radiusKm * 1000}`,
      },
    },
  };
}

export function globalSchemaGraph() {
  return [brandSchema(), organizationSchema(), localBusinessSchema(), webSiteSchema()];
}

export function categoryCollectionSchema(category, products = []) {
  if (!category?.slug) return null;
  const elements = products.slice(0, 12).map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: absoluteUrl(`/product/${p.id}`),
    name: p.name,
  }));
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": absoluteUrl(`/category/${category.slug}#collection`),
    name: `${category.name} | ${SITE_NAME}`,
    description: category.description || `Shop ${category.name} at ${SITE_NAME}`,
    url: absoluteUrl(`/category/${category.slug}`),
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#store` },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: elements,
    },
  };
}
